import React, { useContext, useRef, useState, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from 'react-icons/rx';
import ModalHeading from '../../components/shared/modal-heading';
import MultipleAttachmentInput from '../../components/shared/multiple-attachemnt';
import { AccountsContext } from '../../components/context/account-context';
import { uploadMedia } from '../../services/upload-service';
import { prependDefault } from '../../utils/structure-options-utils';
import { getIncidentMedia, updateIncident } from '../../services/incidents-service';
import Loader from '../../components/shared/loader';
import { FiExternalLink } from 'react-icons/fi';
import { MdContentCopy } from 'react-icons/md';
import GoogleMapComponent from '../../components/shared/google-map';
import Modal from '../../components/shared/modal';

const EditModal = ({ fetchData, typesOptions, globalDataloading, drivers, setData, vehicles, selectedRow, setSelectedRow, setIsEditModalOpen, isVehicleTab = false }) => {
    const { accountId } = useContext(AccountsContext);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [error, setError] = useState();
    const [isUploading, setIsUploading] = useState(false);
    const [fetchingMedia, setFetchingMedia] = useState(false);
    const fileInputRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const [openMapModal, setOpenMapModal] = useState(false);
    const [mapPosition, setMapPosition] = useState(
        selectedRow.location
            ? {
                lat: parseFloat(selectedRow.location.split(",")[0]),
                lng: parseFloat(selectedRow.location.split(",")[1]),
            }
            : { lat: 0, lng: 0 }
    );

    console.log(selectedRow)

    // Handle location change from map
    const handleLocationChange = (newLocationString) => {
        // newLocationString is in "lat,lng" format
        const [lat, lng] = newLocationString.split(",").map(parseFloat);
        if (isNaN(lat) || isNaN(lng)) {
            console.error('Invalid location format:', newLocationString);
            return;
        }
        const newLat = lat.toFixed(6);
        const newLng = lng.toFixed(6);
        const formattedLocation = `${newLat},${newLng}`;
        setMapPosition({ lat: parseFloat(newLat), lng: parseFloat(newLng) });
        handleFieldUpdate(formattedLocation, 'location');
        // setOpenMapModal(false); // Close modal after updating
    };

    // Get the incident media data
    const fetchIncidentMedia = async () => {
        try {
            setFetchingMedia(true);
            const res = await getIncidentMedia(accountId, selectedRow.incidentId);
            if (res.success) {
                const mediaFiles = res.data?.data || [];
                setUploadedFiles(mediaFiles.map(file => ({
                    mediaId: file.mediaId,
                    fileType: file.fileType,
                    fileName: file.fileName,
                    signedUrl: file.signedUrl,
                    signedThumbUrl: file.signedThumbUrl,
                    imagePreview: file.fileType?.startsWith('image/') ? file.signedThumbUrl || file.signedUrl : null
                })));
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to fetch media');
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            setError('An error occurred while fetching media');
        } finally {
            setFetchingMedia(false);
        }
    };


    useEffect(() => {
        fetchIncidentMedia();
    }, [accountId, selectedRow.incidentId]);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle field updates
    const handleFieldUpdate = async (value, fieldName) => {
        if (!selectedRow) return;

        console.log(value, fieldName);

        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        let payload = {};
        if (fieldName === 'vehicle') {
            payload = { vehicleId: value };
        } else if (fieldName === 'causer') {
            payload = { causedBy: value };
        } else if (fieldName === 'incidentTime') {
            payload = { incidentTime: new Date(value).toISOString() }; // ✅ UTC
        } else {
            payload = { [fieldName]: value };
        }

        try {
            console.log(payload);
            const response = await updateIncident(accountId, selectedRow.incidentId, payload);
            console.log(response);

            if (response.success) {
                const updatedRow = { ...selectedRow };

                switch (fieldName) {
                    case 'incidentTime':
                        updatedRow.incidentTime = payload.incidentTime; // ✅ keep UTC format
                        break;

                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicles.find((v) => v.vehicleId === value)?.label || 'N/A',
                        };
                        break;

                    case 'causer':
                        updatedRow.causer = {
                            userId: value,
                            name: drivers.find((d) => d.userId === value)?.label || 'N/A',
                        };
                        break;

                    default:
                        updatedRow[fieldName] = value;
                }

                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) =>
                        row.incidentId === selectedRow.incidentId ? updatedRow : row
                    )
                );

                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success',
                });
            } else {
                setUpdateStatus({
                    message:
                        response.data?.message ||
                        `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Error updating incident:', error);
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error',
            });
        } finally {
            setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };


    // Handle file upload
    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadStatus({ message: '', type: '' });

        try {
            // Upload files to the server
            const uploadPromises = files.map((file) =>
                uploadMedia(accountId, file, 'incident', selectedRow.incidentId)
            );
            const uploadResponses = await Promise.all(uploadPromises);

            // Process responses
            const successfulUploads = uploadResponses.filter(res => res.success);
            const failedUploads = uploadResponses.filter(res => !res.success);
            // console.log(successfulUploads)

            if (failedUploads.length > 0) {
                setUploadStatus({
                    message: `${failedUploads.length} file(s) failed to upload`,
                    type: 'error',
                });
            }

            if (successfulUploads.length > 0) {
                const newFiles = successfulUploads.map(res => ({
                    mediaId: res.data.data.mediaId,
                    fileType: res.data.data.fileType,
                    fileName: res.data.data.fileName,
                    signedUrl: res.data.data.signedUrl,
                    signedThumbUrl: res.data.data.signedThumbUrl,
                    imagePreview: res.data.data.fileType?.startsWith('image/') ? res.data.data.signedThumbUrl || res.data.data.signedUrl : null
                }));

                setUploadedFiles(prev => [...prev, ...newFiles]);
                setUploadStatus({
                    message: `${successfulUploads.length} file(s) uploaded successfully!`,
                    type: 'success',
                });
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            setUploadStatus({
                message: 'Failed to upload files',
                type: 'error',
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file removal
    const handleRemoveFile = async (fileToRemove) => {
        try {
            setUploadedFiles(prev => prev.filter(file => file.mediaId !== fileToRemove.mediaId));
        } catch (error) {
            console.error('Error removing file:', error);
            // Add it back to the list if deletion failed
            setUploadedFiles(prev => [...prev, fileToRemove]);
            setUploadStatus({
                message: 'Failed to delete file',
                type: 'error',
            });
        }
    };

    const vehicleOptions = prependDefault(
        vehicles.map((v) => ({ value: v.vehicleId, label: v.label || v.name })),
        'Vehicle'
    );
    const typesOption = prependDefault(
        typesOptions.map((v) => ({ value: v.slug, label: v.label || v.name })),
        'Types'
    );
    const causedByOptions = prependDefault(
        drivers.map((s) => ({ value: s.userId, label: s.label || s.name })),
        'Causer'
    );


    const labelMap = {
        time: 'Incident Time',
        vehicle: 'Vehicle',
        incidentId: 'Incident ID',
        type: 'Type',
        causer: 'Causer',
        location: 'Location',
        status: 'Status',
        note: 'Note',
    };

    //  Handle copy location
    const handleCopyLocation = () => {
        if (selectedRow?.location) {
            navigator.clipboard.writeText(selectedRow.location).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 3000); // hide after 3s
            });
        }
    };

    const handleLocationEditClick = () => {
        setOpenMapModal(true);
    };

    if (!selectedRow) return null;


    return (
        <div className="space-y-3 xs:px-4 px-2 pt-3 relative">
            <ModalHeading className="text-left mb-3">Incident Details</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full"
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className="text-2xl" />
            </button>

            {(updateStatus.message || uploadStatus.message) && (
                <div
                    className={`px-4 py-2 text-sm ${(updateStatus.type || uploadStatus.type) === 'success'
                        ? 'text-green-600 bg-green-100'
                        : 'text-red-600 bg-red-100'
                        } rounded-md mx-4`}
                >
                    {updateStatus.message || uploadStatus.message}
                </div>
            )}

            {(fetchingMedia || globalDataloading) ? (
                <Loader />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <EditableField
                        label="Time"
                        name="incidentTime"
                        value={selectedRow.incidentTime || ''}
                        onChange={(value) => handleFieldUpdate(value, 'incidentTime')}
                        type="datetime-local"
                        isLoading={loadingFields.incidentTime}
                        allowFutureDates={false}
                    />

                    <EditableField
                        label="Vehicle"
                        name="vehicle"
                        type="select"
                        value={selectedRow?.vehicle?.vehicleId || ''}
                        options={vehicleOptions}
                        onChange={handleFieldUpdate}
                        isEditable={false}
                        isLoading={loadingFields.vehicle}
                    />

                    <EditableField
                        label="Type"
                        name="type"
                        type="select"
                        value={selectedRow.type || ''}
                        options={typesOption}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.type}
                        isEditable={false}
                    />

                    <EditableField
                        label="Causer"
                        name="causer"
                        type="select"
                        value={selectedRow.causer?.userId || ''}
                        options={causedByOptions}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.causer}
                        isEditable={false}
                    />

                    <div className='flex items-center w-full gap-1'>
                        <EditableField
                            label="Location"
                            name="location"
                            value={
                                selectedRow.location
                                    ? (() => {
                                        const [lat, lng] = selectedRow.location.split(",");
                                        const formattedLat = parseFloat(lat).toFixed(3);
                                        const formattedLng = parseFloat(lng).toFixed(3);
                                        return `${formattedLat}, ${formattedLng}`;
                                    })()
                                    : ""
                            }
                            onChange={(value) => handleFieldUpdate(value, "location")}
                            type="text"
                            isLoading={loadingFields.location}
                            onEditClick={handleLocationEditClick}
                        />
                        <div className='flex items-center gap-2 w-16  mt-4  relative'>
                            {copied && (
                                <p className="text-[10px] absolute right-3 -top-4 text-green-500">
                                    Copied!
                                </p>
                            )}
                            <button
                                onClick={() => {
                                    if (selectedRow.location) {
                                        const [lat, lng] = selectedRow.location.split(",");
                                        const url = `https://www.google.com/maps?q=${lat},${lng}`;
                                        window.open(url, "_blank");
                                    }
                                }}
                            >
                                <FiExternalLink className='text-blue-500 hover:text-blue-600' />
                            </button>
                            <button onClick={handleCopyLocation}>
                                <MdContentCopy className='text-green-500 hover:text-green-600' />
                            </button>
                        </div>
                    </div> 

                    <Modal
                        isOpen={openMapModal}
                        onClose={() => setOpenMapModal(false)}
                    >
                        <div className='min-h-[350px]'>
                            <div className='w-full h-5 mb-5 relative'>
                                <ModalHeading>Select Location</ModalHeading>
                                <button
                                    className="hover:scale-105 absolute -top-2 -right-2 bg-white border p-2 rounded-full"
                                    onClick={() => setOpenMapModal(false)}
                                >
                                    <RxCross2 className="text-xl" />
                                </button>
                            </div>
                            <GoogleMapComponent
                                isVisible={openMapModal}
                                initialLocation={mapPosition}
                                onLocationChange={handleLocationChange}
                            />
                        </div>
                    </Modal>

                    <EditableField
                        label="Status"
                        name="status"
                        value={selectedRow.status || 'reported'}
                        onChange={(option) => handleFieldUpdate(option ? option.value : '', 'status')}
                        type="select"
                        isEditable={false}
                        isLoading={loadingFields.status}
                    />

                    <EditableField
                        label="Note"
                        name="note"
                        value={selectedRow.note || ''}
                        onChange={(value) => handleFieldUpdate(value, 'note')}
                        type="textarea"
                        isLoading={loadingFields.note}
                    />

                    <MultipleAttachmentInput
                        handleFileUpload={handleFileUpload}
                        uploadedFiles={uploadedFiles}
                        handleRemoveFile={handleRemoveFile}
                        isUploading={isUploading}
                        accountId={accountId}
                        fileInputRef={fileInputRef}
                    />
                </div>
            )}
        </div>
    );
};

export default EditModal;